import { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
    RawData,
    Reason,
    CompareBar,
    Timeline,
    CONFIDENCE_LABEL,
    REASON_KIND_META,
} from './rawData';

dayjs.locale('ko');

/**
 * 원천 데이터 팝업.
 *
 * 이전 버전은 계산식을 맨 앞에 놓고 비교 기준을 각주에 넣었다.
 * 대표님은 계산하러 오신 게 아니라 판단하러 오셨는데 수식부터 읽어야 했다.
 *
 * 그래서 순서를 뒤집는다.
 *   ① 결론 한 줄
 *   ② 근거 — 번호를 붙이고, 숫자마다 비교 대상을 나란히 놓는다.
 *      "6회"만 보면 알 수 없지만 "이 회원 6회 / 이탈한 회원 7회 / 재등록한 회원 11회"는
 *      설명 없이 눈으로 판정된다.
 *   ③ 수업 간격 타임라인 — 표로는 안 보이는 "벌어짐"을 그림으로
 *   ④ 원천 기록 (접힘) — 의심이 들 때만
 *   ⑤ 계산 방법·용어 (접힘)
 */

const GRADE_TONE: Record<RawData['verdict']['grade'], { accent: string; bg: string; border: string }> = {
    DANGER: { accent: '#FF3E4A', bg: '#FFF5F5', border: '#F8C9CC' },
    WARNING: { accent: '#B45309', bg: '#FFFCF3', border: '#F0D9A8' },
    CHANCE: { accent: '#3D73DD', bg: '#F7FAFF', border: '#C3D7F5' },
    PRAISE: { accent: '#00A793', bg: '#F4FCF9', border: '#B7DFD2' },
};

const BORDER = '#E7EDF6';

/* ───────────────────────── 비교 막대 ───────────────────────── */

/**
 * 이 회원 하나와 기준선들을 같은 축에 놓는다.
 * 색은 셋뿐이다 — 내 회원(진하게) / 나쁜 쪽(붉게) / 좋은 쪽(초록).
 */
const CompareChart = ({ bar, accent }: { bar: CompareBar; accent: string }) => {
    const all = [bar.mine.value, ...bar.others.map((o) => o.value)];
    const max = Math.max(...all) * 1.18 || 1;

    const rows = [
        { label: bar.mine.label, value: bar.mine.value, mine: true, tone: undefined as CompareBar['others'][number]['tone'] },
        ...bar.others.map((o) => ({ label: o.label, value: o.value, mine: false, tone: o.tone })),
    ];

    return (
        <div className="flex flex-col" style={{ gap: 9, marginTop: 13 }}>
            {rows.map((r) => {
                const color = r.mine ? accent : r.tone === 'GOOD' ? '#00A793' : r.tone === 'BAD' ? '#F2A0A6' : '#A8B6CA';
                return (
                    <div key={r.label} className="flex items-center" style={{ gap: 12 }}>
                        <span
                            style={{
                                fontSize: 13,
                                color: r.mine ? '#16202E' : '#7C8794',
                                fontWeight: r.mine ? 700 : 400,
                                width: 178,
                                flex: 'none',
                                textAlign: 'right',
                            }}
                        >
                            {r.label}
                        </span>
                        <div style={{ flex: 1, height: 26, position: 'relative' }}>
                            <div
                                style={{
                                    width: `${(r.value / max) * 100}%`,
                                    height: 26,
                                    background: color,
                                    borderRadius: 5,
                                    minWidth: 4,
                                    transition: 'width .3s',
                                }}
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    left: `calc(${(r.value / max) * 100}% + 9px)`,
                                    top: 3,
                                    fontSize: 15,
                                    fontWeight: r.mine ? 800 : 600,
                                    color: r.mine ? '#16202E' : '#7C8794',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {r.value}
                                {bar.unit}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/* ───────────────────────── 카운트다운 ───────────────────────── */

const Countdown = ({ items }: { items: NonNullable<Reason['countdown']> }) => (
    <div className="flex" style={{ gap: 9, marginTop: 13 }}>
        {items.map((it) => (
            <div
                key={it.label}
                className="flex flex-1 flex-col rounded-[10px]"
                style={{
                    background: it.alert ? '#FFF0F0' : '#F7F9FC',
                    border: `1px solid ${it.alert ? '#FBD0D3' : BORDER}`,
                    padding: '13px 15px',
                }}
            >
                <span style={{ fontSize: 12.5, color: it.alert ? '#C22D38' : '#7C8794' }}>{it.label}</span>
                <span
                    style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: it.alert ? '#C22D38' : '#16202E',
                        letterSpacing: '-0.03em',
                        marginTop: 4,
                    }}
                >
                    {it.value}
                </span>
                {it.caption && (
                    <span style={{ fontSize: 12, color: '#93A0AE', marginTop: 3 }}>{it.caption}</span>
                )}
            </div>
        ))}
    </div>
);

/* ───────────────────────── 수업 간격 타임라인 ───────────────────────── */

const DOT_TONE: Record<string, { fill: string; ring: string }> = {
    출석: { fill: '#3D73DD', ring: '#C3D7F5' },
    노쇼: { fill: '#FF3E4A', ring: '#F8C9CC' },
    취소: { fill: '#C4CCD8', ring: '#E7EDF6' },
};

/**
 * 수업을 날짜 축 위의 점으로 찍는다.
 * 표에서는 "07-27, 07-20, 07-13…"이 그냥 목록이지만,
 * 축 위에 놓으면 오른쪽 끝의 빈 공간이 곧바로 눈에 들어온다.
 */
const LessonTimeline = ({ tl }: { tl: Timeline }) => {
    const start = dayjs(tl.from);
    const end = dayjs(tl.to);
    const span = Math.max(end.diff(start, 'day'), 1);
    const pos = (d: string) => (dayjs(d).diff(start, 'day') / span) * 100;

    const last = tl.dots.length ? tl.dots[tl.dots.length - 1] : null;
    const gapDays = last ? end.diff(dayjs(last.date), 'day') : 0;

    /** 월 눈금 */
    const ticks: { at: number; label: string }[] = [];
    let cur = start.startOf('month');
    while (cur.isBefore(end)) {
        if (!cur.isBefore(start)) ticks.push({ at: pos(cur.format('YYYY-MM-DD')), label: cur.format('M월') });
        cur = cur.add(1, 'month');
    }

    return (
        <div className="rounded-[13px]" style={{ border: `1px solid ${BORDER}`, padding: '18px 20px 14px' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#16202E' }}>{tl.title}</span>

            <div style={{ position: 'relative', height: 62, marginTop: 20 }}>
                {/* 축 */}
                <div
                    style={{ position: 'absolute', left: 0, right: 0, top: 21, height: 2, background: '#EDF1F7' }}
                />

                {/* 마지막 수업 이후 공백 구간 강조 */}
                {last && gapDays >= 7 && (
                    <div
                        style={{
                            position: 'absolute',
                            left: `${pos(last.date)}%`,
                            right: 0,
                            top: 12,
                            height: 20,
                            background: 'repeating-linear-gradient(45deg,#FFE8E9,#FFE8E9 5px,#FFF5F5 5px,#FFF5F5 10px)',
                            borderRadius: 4,
                        }}
                    />
                )}

                {/* 월 눈금 */}
                {ticks.map((t) => (
                    <span
                        key={t.label}
                        style={{
                            position: 'absolute',
                            left: `${t.at}%`,
                            top: 40,
                            fontSize: 11.5,
                            color: '#A7B0BC',
                            transform: 'translateX(-50%)',
                        }}
                    >
                        {t.label}
                    </span>
                ))}

                {/* 수업 점 */}
                {tl.dots.map((d) => {
                    const tone = DOT_TONE[d.status];
                    return (
                        <span
                            key={d.date + d.status}
                            title={`${dayjs(d.date).format('M월 D일 (ddd)')} · ${d.status}`}
                            style={{
                                position: 'absolute',
                                left: `${pos(d.date)}%`,
                                top: 15,
                                width: 14,
                                height: 14,
                                borderRadius: 7,
                                background: tone.fill,
                                border: `3px solid ${tone.ring}`,
                                transform: 'translateX(-50%)',
                                cursor: 'help',
                            }}
                        />
                    );
                })}

                {/* 공백 일수 라벨 */}
                {last && gapDays >= 7 && (
                    <span
                        style={{
                            position: 'absolute',
                            left: `calc(${pos(last.date)}% + ${(100 - pos(last.date)) / 2}%)`,
                            top: -4,
                            fontSize: 12.5,
                            fontWeight: 800,
                            color: '#C22D38',
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {gapDays}일째 수업 없음
                    </span>
                )}
            </div>

            <p style={{ fontSize: 14, color: '#3A4757', lineHeight: '21px', marginTop: 10 }}>{tl.note}</p>

            <div className="flex items-center" style={{ gap: 14, marginTop: 11 }}>
                {(['출석', '노쇼', '취소'] as const).map((s) => (
                    <span key={s} className="flex items-center" style={{ gap: 5 }}>
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 5,
                                background: DOT_TONE[s].fill,
                                border: `2px solid ${DOT_TONE[s].ring}`,
                            }}
                        />
                        <span style={{ fontSize: 12, color: '#93A0AE' }}>{s}</span>
                    </span>
                ))}
                <span style={{ fontSize: 12, color: '#93A0AE', marginLeft: 4 }}>점에 마우스를 올리면 날짜가 보입니다</span>
            </div>
        </div>
    );
};

/* ───────────────────────── 근거 블록 ───────────────────────── */

const ReasonBlock = ({ reason, index, accent }: { reason: Reason; index: number; accent: string }) => {
    const kind = REASON_KIND_META[reason.kind];
    const conf = reason.confidence ? CONFIDENCE_LABEL[reason.confidence] : null;

    return (
        <div
            className="rounded-[13px]"
            style={{ border: `1px solid ${BORDER}`, background: '#FFFFFF', padding: '18px 20px' }}
        >
            <div className="flex items-start" style={{ gap: 12 }}>
                <span
                    className="flex flex-none items-center justify-center"
                    style={{
                        width: 26,
                        height: 26,
                        borderRadius: 13,
                        background: '#16202E',
                        color: '#FFFFFF',
                        fontSize: 13.5,
                        fontWeight: 800,
                        marginTop: 1,
                    }}
                >
                    {index}
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex flex-wrap items-center" style={{ gap: 7 }}>
                        <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E', letterSpacing: '-0.01em' }}>
                            {reason.title}
                        </span>
                        <span
                            className="rounded-[5px]"
                            title={kind.desc}
                            style={{
                                background: kind.bg,
                                color: kind.color,
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: '3px 8px',
                                cursor: 'help',
                            }}
                        >
                            {kind.label}
                        </span>
                        {conf && (
                            <span
                                className="rounded-[5px]"
                                title={conf.desc}
                                style={{
                                    background: conf.bg,
                                    color: conf.color,
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    padding: '3px 8px',
                                    cursor: 'help',
                                }}
                            >
                                {conf.text}
                            </span>
                        )}
                    </div>

                    <p style={{ fontSize: 14.5, color: '#3A4757', lineHeight: '22px', marginTop: 7 }}>
                        {reason.plain}
                    </p>

                    {reason.compare && <CompareChart bar={reason.compare} accent={accent} />}
                    {reason.countdown && <Countdown items={reason.countdown} />}
                </div>
            </div>
        </div>
    );
};

/* ───────────────────────── 접이식 ───────────────────────── */

const Collapsible = ({
    title,
    hint,
    children,
}: {
    title: string;
    hint: string;
    children: React.ReactNode;
}) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-[13px]" style={{ border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center"
                style={{
                    padding: '15px 20px',
                    background: open ? '#F7F9FC' : '#FFFFFF',
                    border: 'none',
                    cursor: 'pointer',
                    gap: 10,
                    textAlign: 'left',
                }}
            >
                <span style={{ fontSize: 15.5, fontWeight: 700, color: '#16202E' }}>{title}</span>
                <span style={{ fontSize: 13, color: '#93A0AE' }}>{hint}</span>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#5B6B7F', fontWeight: 600 }}>
                    {open ? '접기 ▲' : '펼치기 ▼'}
                </span>
            </button>
            {open && <div style={{ padding: '4px 20px 20px', borderTop: `1px solid ${BORDER}` }}>{children}</div>}
        </div>
    );
};

/* ───────────────────────── 본체 ───────────────────────── */

type Props = {
    data: RawData;
    /**
     * 헤더 아래 붙는 알림 한 줄.
     * 지금은 "이 근거는 다른 날짜 브리핑의 것"이라는 안내에 씁니다 —
     * 서버가 itemId 로 가장 최근 날짜를 찾아 주기 때문에, 지난 브리핑을 열어보면
     * 오늘 근거가 뜰 수 있습니다. 말 안 하면 원장님은 그날 판단으로 읽습니다.
     */
    notice?: React.ReactNode;
    onClose: () => void;
};

const RawDataModal = ({ data, notice, onClose }: Props) => {
    const tone = GRADE_TONE[data.verdict.grade];

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            /*
              상세 드로어(2300) 위, 지시 확인 창(2500) 아래.
              브리핑 상세가 드로어 안에서 열리면서 이 팝업이 그 위에서 불린다 —
              뒤에 깔리면 "왜 이렇게 판단했는지 보기" 를 눌러도 아무 일이 안 일어나 보인다.
            */
            style={{ background: 'rgba(12,22,40,0.6)', zIndex: 2400, padding: 24 }}
            onClick={onClose}
        >
            <div
                className="flex flex-col overflow-hidden rounded-[18px] bg-white"
                style={{ width: 880, maxHeight: '92vh', boxShadow: '0 28px 72px rgba(10,20,40,0.36)' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 헤더 */}
                <div
                    className="flex flex-none items-start justify-between"
                    style={{ padding: '24px 30px 20px', borderBottom: `1px solid ${BORDER}` }}
                >
                    <div className="flex flex-col">
                        <span style={{ fontSize: 22, fontWeight: 800, color: '#16202E', letterSpacing: '-0.02em' }}>
                            {data.title}
                        </span>
                        <span style={{ fontSize: 14, color: '#7C8794', marginTop: 5 }}>{data.subtitle}</span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="닫기"
                        style={{
                            border: `1px solid ${BORDER}`,
                            background: '#FFFFFF',
                            color: '#7C8794',
                            width: 32,
                            height: 32,
                            borderRadius: 9,
                            cursor: 'pointer',
                            fontSize: 17,
                            lineHeight: '30px',
                        }}
                    >
                        ×
                    </button>
                </div>

                {notice && (
                    <div
                        className="flex flex-none items-center"
                        style={{
                            padding: '10px 30px',
                            background: '#FFF8E8',
                            borderBottom: `1px solid ${BORDER}`,
                            gap: 8,
                        }}
                    >
                        <span style={{ fontSize: 13 }}>ℹ️</span>
                        <span style={{ fontSize: 12.5, color: '#8A5A00' }}>{notice}</span>
                    </div>
                )}

                {/*
                  본문.
                  스크롤 영역 자체를 flex 컨테이너로 두면 자식들이 눌려버린다(접이식 섹션이 납작해짐).
                  그래서 스크롤은 block 이 맡고, 간격은 안쪽 래퍼가 맡는다.
                */}
                <div className="overflow-y-auto" style={{ padding: '20px 30px 26px', flex: 1, minHeight: 0 }}>
                    <div className="flex flex-col" style={{ gap: 12 }}>
                    {/* ① 결론 */}
                    <div
                        className="rounded-[13px]"
                        style={{
                            background: tone.bg,
                            border: `1px solid ${tone.border}`,
                            borderLeft: `4px solid ${tone.accent}`,
                            padding: '18px 22px',
                        }}
                    >
                        <span style={{ fontSize: 19, fontWeight: 800, color: tone.accent, letterSpacing: '-0.02em' }}>
                            {data.verdict.headline}
                        </span>
                        <p style={{ fontSize: 14.5, color: '#3A4757', lineHeight: '22px', marginTop: 7 }}>
                            {data.verdict.body}
                        </p>
                    </div>

                    {/* ② 근거 */}
                    <div className="flex items-baseline" style={{ gap: 9, marginTop: 4 }}>
                        <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E' }}>
                            이렇게 판단한 이유
                        </span>
                        <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                            {data.reasons.length}가지 · 숫자 옆의 회색 막대가 비교 기준입니다
                        </span>
                    </div>
                    {data.reasons.map((r, i) => (
                        <ReasonBlock key={r.title} reason={r} index={i + 1} accent={tone.accent} />
                    ))}

                    {/* ③ 타임라인 */}
                    {data.timeline && (
                        <>
                            <div className="flex items-baseline" style={{ gap: 9, marginTop: 4 }}>
                                <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E' }}>
                                    수업 기록으로 보기
                                </span>
                                <span style={{ fontSize: 13.5, color: '#93A0AE' }}>최근 두 달</span>
                            </div>
                            <LessonTimeline tl={data.timeline} />
                        </>
                    )}

                    {/* ④ 원천 기록 */}
                    <div style={{ marginTop: 6 }} />
                    <Collapsible title="확인한 원본 기록" hint={`${data.sections.length}개 항목 · 수업·수강권·요청 내역`}>
                        <div className="flex flex-col" style={{ gap: 22, marginTop: 18 }}>
                            {data.sections.map((s) => {
                                const conf = s.confidence ? CONFIDENCE_LABEL[s.confidence] : null;
                                return (
                                    <div key={s.label}>
                                        <div className="flex items-center" style={{ gap: 8 }}>
                                            <span style={{ fontSize: 15, fontWeight: 700, color: '#16202E' }}>
                                                {s.label}
                                            </span>
                                            {conf && (
                                                <span
                                                    className="rounded-[5px]"
                                                    title={conf.desc}
                                                    style={{
                                                        background: conf.bg,
                                                        color: conf.color,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        padding: '2px 7px',
                                                        cursor: 'help',
                                                    }}
                                                >
                                                    {conf.text}
                                                </span>
                                            )}
                                        </div>

                                        {s.benchmark && (
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    color: '#5B6B7F',
                                                    display: 'block',
                                                    marginTop: 6,
                                                    lineHeight: '19px',
                                                }}
                                            >
                                                기준 · {s.benchmark}
                                            </span>
                                        )}

                                        <table
                                            style={{
                                                width: '100%',
                                                borderCollapse: 'collapse',
                                                marginTop: 9,
                                                border: `1px solid ${BORDER}`,
                                                borderRadius: 8,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <thead>
                                                <tr style={{ background: '#F7F9FC' }}>
                                                    {s.columns.map((c, ci) => (
                                                        <th
                                                            key={c}
                                                            style={{
                                                                padding: '9px 13px',
                                                                fontSize: 12.5,
                                                                fontWeight: 700,
                                                                color: '#7C8794',
                                                                textAlign: ci === 0 ? 'left' : 'right',
                                                                borderBottom: `1px solid ${BORDER}`,
                                                            }}
                                                        >
                                                            {c}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {s.rows.map((r, ri) => (
                                                    <tr key={r.cells.join('|')}>
                                                        {r.cells.map((cell, ci) => (
                                                            <td
                                                                key={ci}
                                                                style={{
                                                                    padding: '9px 13px',
                                                                    fontSize: 13.5,
                                                                    color: r.muted
                                                                        ? '#A7B0BC'
                                                                        : r.alert && ci > 0
                                                                          ? '#C22D38'
                                                                          : '#3A4757',
                                                                    fontWeight: r.alert && ci > 0 ? 700 : 400,
                                                                    textAlign: ci === 0 ? 'left' : 'right',
                                                                    borderTop:
                                                                        ri === 0 ? 'none' : '1px solid #F1F4F9',
                                                                }}
                                                            >
                                                                {cell}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {s.note && (
                                            <span
                                                style={{
                                                    fontSize: 12.5,
                                                    color: '#7C8794',
                                                    display: 'block',
                                                    marginTop: 7,
                                                    lineHeight: '18px',
                                                }}
                                            >
                                                {s.note}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Collapsible>

                    {/* ⑤ 계산 방법 · 용어 */}
                    <Collapsible title="계산 방법과 용어" hint="숫자가 어떻게 나왔는지 궁금하실 때">
                        <div className="flex flex-col" style={{ gap: 18, marginTop: 18 }}>
                            {data.glossary && data.glossary.length > 0 && (
                                <div>
                                    <span style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E' }}>용어</span>
                                    <div className="flex flex-col" style={{ gap: 10, marginTop: 9 }}>
                                        {data.glossary.map((g) => (
                                            <div
                                                key={g.term}
                                                className="rounded-[9px]"
                                                style={{ background: '#F7F9FC', padding: '11px 14px' }}
                                            >
                                                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#16202E' }}>
                                                    {g.term}
                                                </span>
                                                <p
                                                    style={{
                                                        fontSize: 13.5,
                                                        color: '#5B6B7F',
                                                        marginTop: 3,
                                                        lineHeight: '20px',
                                                    }}
                                                >
                                                    {g.desc}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.sections.some((s) => s.formula) && (
                                <div>
                                    <span style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E' }}>계산식</span>
                                    <div className="flex flex-col" style={{ gap: 8, marginTop: 9 }}>
                                        {data.sections
                                            .filter((s) => s.formula)
                                            .map((s) => (
                                                <div
                                                    key={s.label}
                                                    className="rounded-[9px]"
                                                    style={{ border: `1px solid ${BORDER}`, padding: '11px 14px' }}
                                                >
                                                    <span style={{ fontSize: 12.5, color: '#7C8794' }}>{s.label}</span>
                                                    <p
                                                        style={{
                                                            fontSize: 13.5,
                                                            color: '#2C3A4B',
                                                            marginTop: 3,
                                                            fontFamily:
                                                                'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                        }}
                                                    >
                                                        {s.formula}
                                                    </p>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <span style={{ fontSize: 14.5, fontWeight: 700, color: '#16202E' }}>
                                    근거 표시의 뜻
                                </span>
                                <div className="flex flex-col" style={{ gap: 7, marginTop: 9 }}>
                                    {Object.values(REASON_KIND_META).map((k) => (
                                        <div key={k.label} className="flex items-start" style={{ gap: 9 }}>
                                            <span
                                                className="flex-none rounded-[5px]"
                                                style={{
                                                    background: k.bg,
                                                    color: k.color,
                                                    fontSize: 11.5,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    minWidth: 68,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {k.label}
                                            </span>
                                            <span style={{ fontSize: 13, color: '#5B6B7F', lineHeight: '19px' }}>
                                                {k.desc}
                                            </span>
                                        </div>
                                    ))}
                                    {Object.values(CONFIDENCE_LABEL).map((c) => (
                                        <div key={c.text} className="flex items-start" style={{ gap: 9 }}>
                                            <span
                                                className="flex-none rounded-[5px]"
                                                style={{
                                                    background: c.bg,
                                                    color: c.color,
                                                    fontSize: 11.5,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    minWidth: 68,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {c.text}
                                            </span>
                                            <span style={{ fontSize: 13, color: '#5B6B7F', lineHeight: '19px' }}>
                                                {c.desc}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Collapsible>
                    </div>
                </div>

                {/* 푸터 */}
                <div
                    className="flex flex-none items-center justify-between"
                    style={{ padding: '15px 30px', borderTop: `1px solid ${BORDER}`, background: '#FCFDFE' }}
                >
                    <span style={{ fontSize: 13, color: '#93A0AE' }}>
                        브리핑 판단에 실제로 사용된 원천 데이터입니다
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-[9px]"
                        style={{
                            height: 40,
                            padding: '0 24px',
                            background: '#1A305D',
                            border: 'none',
                            color: '#FFFFFF',
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RawDataModal;
